import * as psl from 'psl';
import { useMemo } from 'react';

import { useMy } from '@/hooks';
import { useGetFile } from '@/hooks/useFiles';
import { useGenericSearch } from '@/hooks/useGenericSearch';
import { useMaskingUserType } from '@/hooks/useMaskingUserType';
import { useSeedProvenanceQuery } from '@/sections/seeds/hooks/drawer/useSeedProvenanceQuery';
import {
  processSeedProvenance,
  ProvenanceNode,
} from '@/sections/seeds/utils/provenanceProcessing';
import { Access, Attribute, ResourceType, Seed } from '@/types';
import { useMergeStatus } from '@/utils/api';
import { getAccessEmail } from '@/utils/getAccessEmail';
import { maskPraetorianName } from '@/utils/userDisplay.util';

/**
 * Coordinated seed drawer data fetching hook
 *
 * KEY-BASED PATTERN: Fetches seed data internally using seedKey, eliminating
 * race conditions from dual state sources. The URL param (seedDrawerKey) is
 * the single source of truth.
 *
 * Manages parallel data fetching for the SeedDrawer with unified state:
 * - Seed data (fetched by key - single source of truth)
 * - Attributes for the seed (source information, discovery metadata)
 * - WHOIS details for domain/IP display
 * - WHOIS history for historical tracking
 * - Access information for user lookups
 *
 * Performance strategy:
 * - All queries run in parallel via React Query's automatic batching
 * - Loading states unified to prevent UI flicker during drawer transitions
 * - Error states consolidated for consistent error handling
 * - Queries disabled when seedKey is empty (drawer closed)
 * - Refetch coordinated across all queries
 *
 * Data transformations:
 * - Computes origination information from seed source and attributes
 * - Formats WHOIS details for display (string or JSON formatting)
 * - Extracts domain from seed identifier for file lookups
 *
 * @param seedKey - The seed key to fetch data for (from URL param seedDrawerKey)
 * @returns Coordinated data, loading states, errors, and refetch utilities
 *
 * @example
 * ```tsx
 * const {
 *   seed,
 *   attributes,
 *   displayDetails,
 *   history,
 *   accesss,
 *   originationInfo,
 *   isInitialLoading,
 *   error,
 *   refetch
 * } = useSeedDrawerData(seedDrawerKey);
 *
 * // Use unified loading state
 * if (isInitialLoading) return <Loader />;
 *
 * // Use data for drawer content
 * <SeedDrawerContent
 *   seed={seed}
 *   displayDetails={displayDetails}
 *   history={history}
 *   originationInfo={originationInfo}
 * />
 * ```
 */
export type WhoisHistoryStatus = 'covered' | 'empty' | 'error' | 'unregistered';

export interface ParsedHistory {
  status: WhoisHistoryStatus | undefined;
  records: unknown[] | undefined;
}

// Details file is the definitive unregistered marker: {"status":"unregistered"}.
// Narrow on unknown — useGetFile auto-parses JSON, so data may be any shape.
function isUnregisteredDetailsEnvelope(raw: unknown): boolean {
  return (
    typeof raw === 'object' &&
    raw !== null &&
    'status' in raw &&
    (raw as { status: unknown }).status === 'unregistered'
  );
}

export function parseWhoisHistoryEnvelope(raw: unknown): ParsedHistory {
  if (!raw) return { status: undefined, records: undefined };

  if (typeof raw === 'object' && raw !== null && 'status' in raw) {
    const envelope = raw as { status: string; records?: unknown[] };
    return {
      status: envelope.status as WhoisHistoryStatus,
      records: envelope.records,
    };
  }

  // Legacy format: raw array of records (before envelope migration)
  if (Array.isArray(raw)) {
    return { status: 'covered', records: raw as unknown[] };
  }

  return { status: undefined, records: undefined };
}

export const useSeedDrawerData = (seedKey: string) => {
  // Masking user type for identity privacy (customers see 'The Praetorian Guard')
  const maskingUserType = useMaskingUserType();

  // Query 1: Fetch main seed data by key (single source of truth)
  const seedQuery = useMy(
    {
      query: {
        node: {
          labels: ['Seed'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: seedKey || '',
            },
          ],
        },
      },
      resource: ResourceType.Seed,
    },
    {
      enabled: Boolean(seedKey),
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );
  const seedData = seedQuery.data ?? [];
  const seedStatus = seedQuery.status;

  // Extract seed from response
  const seed = seedData[0] || null;

  // Extract the WHOIS file key for this seed. WHOIS files are stored by
  // registrable domain for domain/tld seeds (the backend collapses subdomains
  // to eTLD+1 on write, e.g. api.example.com -> whois/example.com) and by raw
  // identifier for every other class (ipv4/ipv6/ip/cidr/webapplication/preseed),
  // where psl.get() would return truthy garbage for bare IPs.
  const whoisFileKey = seed
    ? seed.class === 'domain' || seed.class === 'tld'
      ? psl.get(seed.identifier) || seed.identifier
      : seed.identifier
    : null;

  // 1. Fetch attributes for the seed (source information, discovery metadata)
  const attributesQuery = useMy(
    {
      resource: ResourceType.Attribute,
      query: {
        node: {
          labels: ['Attribute'],
          filters: [
            {
              field: 'source',
              operator: '=',
              value: `#asset#${seed?.identifier}`,
            },
          ],
        },
      },
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 2. Fetch WHOIS details (for details tab display)
  const detailsQuery = useGetFile(
    {
      name: `whois/${whoisFileKey}`,
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 3. Fetch WHOIS history (for history tab display)
  const historyQuery = useGetFile(
    {
      name: `whois-history/${whoisFileKey}`,
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 4. Fetch access information (for user email lookups)
  const accessQuery = useGenericSearch(
    {
      query: '#access',
    },
    {
      enabled: !!seed,
      staleTime: 0, // Always fetch fresh data for drawer
    }
  );

  // 5. Extract access array from generic search response
  const accesss = useMemo(
    () => accessQuery.data?.accesss || [],
    [accessQuery.data?.accesss]
  );

  // 5b. Fetch DISCOVERED chain for provenance
  const provenanceQuery = useSeedProvenanceQuery(seedKey || '');

  const seedCapabilities = useMemo(() => {
    if (!seed) return [];
    const raw = (seed as unknown as Record<string, unknown>).capability;
    if (Array.isArray(raw)) return raw as string[];
    return [];
  }, [seed]);

  const provenanceChain = useMemo(
    () =>
      processSeedProvenance(
        provenanceQuery.data,
        seedKey || '',
        seedCapabilities
      ),
    [provenanceQuery.data, seedKey, seedCapabilities]
  );

  // 6. Format WHOIS details for display. The unregistered envelope returns ''
  // so DetailsTab reaches its blank-state branch instead of stringifying it.
  const displayDetails = useMemo(() => {
    const details = detailsQuery.data;
    if (isUnregisteredDetailsEnvelope(details)) return '';
    return typeof details === 'string'
      ? details
      : JSON.stringify(details, null, 2);
  }, [detailsQuery.data]);

  // 6b. Parse WHOIS history envelope (structured file: {status, records})
  const parsedHistory = useMemo(
    () => parseWhoisHistoryEnvelope(historyQuery.data),
    [historyQuery.data]
  );

  // 6c. The details file is the only current-truth signal: the backend
  // overwrites whois/<domain> on every outcome (raw text when registered,
  // {"status":"unregistered"} when not), while whois-history/<domain> is
  // preservation-guarded and can hold a stale unregistered marker after a
  // re-registration whose history fetch failed. Never derive from history.
  const isDomainUnregistered = isUnregisteredDetailsEnvelope(detailsQuery.data);

  // 7. Compute origination information (manual additions + discovery sources)
  const originationInfo = useMemo(() => {
    const discoveries = new Set<string>();

    if (!seed) return [];

    // Check if manually added (source contains email)
    if (seed.source?.includes('@')) {
      const by = maskPraetorianName(
        getAccessEmail(accesss, seed.origin) || '',
        undefined,
        maskingUserType
      );
      discoveries.add(`Added by ${by}`);
    }

    // Check for source attributes (discovery methods)
    attributesQuery.data?.forEach(attr => {
      if (attr.name === 'source' && seed.identifier) {
        // Skip if the source value references the seed itself
        if (attr.value.includes(seed.identifier)) {
          return;
        }

        // Format source info
        if (attr.value === 'provided') {
          discoveries.add('Manually added');
        } else if (!attr.value.startsWith('#')) {
          // For integration discoveries (NS1, Whoxy, etc)
          discoveries.add(`Discovered by ${attr.value}`);
        }
      }
    });

    return Array.from(discoveries);
  }, [seed, attributesQuery.data, accesss, maskingUserType]);

  // 8. Merge loading states (unified loading experience)
  // Include seedStatus since seed is now fetched internally
  const isLoading = useMergeStatus(
    seedStatus,
    attributesQuery.status,
    detailsQuery.status,
    historyQuery.status,
    accessQuery.status,
    provenanceQuery.status
  );

  // Initial loading state (seed must be loaded before showing content)
  const isInitialLoading = seedStatus === 'pending';

  // 9. Consolidate error states (first error takes precedence)
  const error =
    attributesQuery.error ||
    detailsQuery.error ||
    historyQuery.error ||
    accessQuery.error ||
    null;

  // 10. Coordinated refetch function
  const refetch = () => {
    seedQuery.refetch();
    attributesQuery.refetch();
    detailsQuery.refetch();
    historyQuery.refetch();
    accessQuery.refetch();
    provenanceQuery.refetch();
  };

  return {
    // Data - seed is now fetched internally (single source of truth)
    seed,
    attributes: attributesQuery.data,
    displayDetails,
    history: parsedHistory.records,
    whoisHistoryStatus: parsedHistory.status,
    isDomainUnregistered,
    accesss,
    originationInfo,
    provenanceChain,

    // Individual query statuses (for granular loading indicators)
    seedStatus,
    attributesStatus: attributesQuery.status,
    detailsStatus: detailsQuery.status,
    historyStatus: historyQuery.status,
    accessStatus: accessQuery.status,

    // Unified loading states
    isLoading: isLoading === 'pending',
    isInitialLoading,
    isAttributesLoading: attributesQuery.status === 'pending',
    isDetailsLoading: detailsQuery.status === 'pending',
    isHistoryLoading: historyQuery.status === 'pending',
    isAccessLoading: accessQuery.status === 'pending',

    // Error states
    error,
    attributesError: attributesQuery.error || null,
    detailsError: detailsQuery.error || null,
    historyError: historyQuery.error || null,
    accessError: accessQuery.error || null,

    // Utilities
    refetch,
    refetchSeed: seedQuery.refetch,
    refetchAttributes: attributesQuery.refetch,
    refetchDetails: detailsQuery.refetch,
    refetchHistory: historyQuery.refetch,
    refetchAccess: accessQuery.refetch,
  };
};

/**
 * Return type for useSeedDrawerData hook
 */
export interface UseSeedDrawerDataReturn {
  // Data - seed is now fetched internally (single source of truth)
  seed: Seed | null;
  attributes: Attribute[] | undefined;
  displayDetails: string;
  history: unknown[] | undefined;
  whoisHistoryStatus: WhoisHistoryStatus | undefined;
  isDomainUnregistered: boolean;
  accesss: Access[];
  originationInfo: string[];
  provenanceChain: ProvenanceNode[];

  // Individual query statuses
  seedStatus: 'pending' | 'error' | 'success';
  attributesStatus: 'pending' | 'error' | 'success';
  detailsStatus: 'pending' | 'error' | 'success';
  historyStatus: 'pending' | 'error' | 'success';
  accessStatus: 'pending' | 'error' | 'success';

  // Unified loading states
  isLoading: boolean;
  isInitialLoading: boolean;
  isAttributesLoading: boolean;
  isDetailsLoading: boolean;
  isHistoryLoading: boolean;
  isAccessLoading: boolean;

  // Error states
  error: Error | null;
  attributesError: Error | null;
  detailsError: Error | null;
  historyError: Error | null;
  accessError: Error | null;

  // Utilities
  refetch: () => void;
  refetchSeed: () => void;
  refetchAttributes: () => void;
  refetchDetails: () => void;
  refetchHistory: () => void;
  refetchAccess: () => void;
}
